import React from "react";

/**
 * Converts a CSS string like "display: flex; padding: 10px;" into a React
 * style object like { display: "flex", padding: "10px" }.
 *
 * @param {string} cssString
 * @returns {Object}
 */
const parseCssString = (cssString) => {
  const style = {};

  cssString.split(";").forEach((declaration) => {
    const trimmed = declaration.trim();
    if (!trimmed) {
      return;
    }

    const colonIndex = trimmed.indexOf(":");
    if (colonIndex === -1) {
      return;
    }

    const property = trimmed.slice(0, colonIndex).trim();
    const value = trimmed.slice(colonIndex + 1).trim();

    const camelCase = property.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );

    style[camelCase] = value;
  });

  return style;
};

/**
 * Normalizes props from the JSON tree for React rendering.
 * Converts CSS string style props to React style objects.
 *
 * @param {Object} props
 * @returns {Object}
 */
const normalizeProps = (props) => {
  if (!props || typeof props !== "object") {
    return {};
  }

  if (typeof props.style === "string") {
    return { ...props, style: parseCssString(props.style) };
  }

  return props;
};

/**
 * Recursively renders a JSON dashboard tree into React elements.
 *
 * @param {Object|string} node - A tree node or string (text content)
 * @param {Object} registry - Map of type names to React components/HTML tags
 * @param {number} [index=0] - Sibling index used as React key
 * @returns {React.ReactElement|string|null}
 */
export const renderDashboardTree = (node, registry, index = 0) => {
  if (typeof node === "string") {
    return node;
  }

  if (!node || typeof node !== "object" || !node.type) {
    return null;
  }

  const { type, props = {}, children = [] } = node;
  const Component = registry[type] || type;
  const normalizedProps = normalizeProps(props);

  const renderedChildren = children.map((child, i) =>
    renderDashboardTree(child, registry, i)
  );

  if (renderedChildren.length === 0) {
    return <Component key={index} {...normalizedProps} />;
  }

  return (
    <Component key={index} {...normalizedProps}>
      {renderedChildren}
    </Component>
  );
};

export { parseCssString };
